import {useHistory, useLocation} from "react-router-dom";
import {useCallback, useMemo, useRef} from "react";
import queryString from "query-string";
import {deepEqual} from "../../../core/services/utils/utils";

/**
 * Recursively flattens an object.
 * For example:
 * { customer: { id: 1, name: 'cus' } }
 * becomes:
 * { 'customer.id': 1, 'customer.name': 'cus' }
 */
function flattenObject(obj, prefix = '') {
    let result = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const newKey = prefix ? `${prefix}.${key}` : key;
            // Only flatten if the value is a non-null object (and not an array)
            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                Object.assign(result, flattenObject(obj[key], newKey));
            } else {
                result[newKey] = obj[key];
            }
        }
    }
    return result;
}

/**
 * Recursively reconstructs a nested object from a flattened one.
 * For example:
 * { 'customer.id': 1, 'customer.name': 'cus' }
 * becomes:
 * { customer: { id: 1, name: 'cus' } }
 */
function unflattenObject(obj) {
    let result = {};
    for (const flatKey in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, flatKey)) {
            const keys = flatKey.split('.');
            keys.reduce((acc, key, index) => {
                if (index === keys.length - 1) {
                    acc[key] = obj[flatKey];
                    return null;
                }
                if (!acc[key] || typeof acc[key] !== 'object') {
                    acc[key] = {};
                }
                return acc[key];
            }, result);
        }
    }
    return result;
}

/**
 * Hook for React-router-dom Query.
 *
 * BUG: can not simultaneously update multiple times in the same render cycle. needs the ref to be updated prior to the next render cycle.
 *
 * @template T
 * @param {Parameters<import('query-string').parse>[1]} parseOptions
 * @param {Parameters<import('query-string').stringify>[1]} stringifyOptions
 * @returns {[import('query-string').ParsedQuery<T>, (function(Dispatch<SetStateAction<Record<string, any>>>, {location?: LocationDescriptor<unknown>, replace?: boolean}=): void)]}
 */
const useQuery = ({
                      parseOptions = {arrayFormat: 'bracket'},
                      stringifyOptions = {arrayFormat: 'bracket'}
                  } = {}) => {
    const location = useLocation();
    const history = useHistory();

    const parseOptionsExist = useMemo(() => !!parseOptions, [parseOptions]);
    const stringifyOptionsExist = useMemo(() => !!stringifyOptions, [stringifyOptions]);

    /**@type {Parameters<import('query-string').parse>[1]}*/
    const memoizedParseOptions = useMemo(() => !parseOptionsExist ? undefined : {
        ...(parseOptions?.decode !== undefined && {decode: parseOptions?.decode}),
        ...(parseOptions?.arrayFormat !== undefined && {arrayFormat: parseOptions?.arrayFormat}),
        ...(parseOptions?.arrayFormatSeparator !== undefined && {arrayFormatSeparator: parseOptions?.arrayFormatSeparator}),
        ...(parseOptions?.sort !== undefined && {sort: parseOptions?.sort}),
        ...(parseOptions?.parseNumbers !== undefined && {parseNumbers: parseOptions?.parseNumbers}),
        ...(parseOptions?.parseBooleans !== undefined && {parseBooleans: parseOptions?.parseBooleans}),
        ...(parseOptions?.parseFragmentIdentifier !== undefined && {parseFragmentIdentifier: parseOptions?.parseFragmentIdentifier}),
    }, [parseOptionsExist, parseOptions?.decode, parseOptions?.arrayFormat, parseOptions?.arrayFormatSeparator, parseOptions?.sort, parseOptions?.parseNumbers, parseOptions?.parseBooleans, parseOptions?.parseFragmentIdentifier,]);

    /**@type {Parameters<import('query-string').stringify>[1]}*/
    const memoizedStringifyOptions = useMemo(() => !stringifyOptionsExist ? undefined : {
        ...(stringifyOptions?.strict !== undefined && {strict: stringifyOptions?.strict}),
        ...(stringifyOptions?.encode !== undefined && {encode: stringifyOptions?.encode}),
        ...(stringifyOptions?.arrayFormat !== undefined && {arrayFormat: stringifyOptions?.arrayFormat}),
        ...(stringifyOptions?.arrayFormatSeparator !== undefined && {arrayFormatSeparator: stringifyOptions?.arrayFormatSeparator}),
        ...(stringifyOptions?.sort !== undefined && {sort: stringifyOptions?.sort}),
        ...(stringifyOptions?.skipNull !== undefined && {skipNull: stringifyOptions?.skipNull}),
        ...(stringifyOptions?.skipEmptyString !== undefined && {skipEmptyString: stringifyOptions?.skipEmptyString}),
    }, [stringifyOptionsExist, stringifyOptions?.strict, stringifyOptions?.encode, stringifyOptions?.arrayFormat, stringifyOptions?.arrayFormatSeparator, stringifyOptions?.sort, stringifyOptions?.skipNull, stringifyOptions?.skipEmptyString])

    const historyRef = useRef(history);
    const locationRef = useRef(location);
    locationRef.current = location;
    historyRef.current = history;

    const parseQuery = useCallback(search => {
        const parsed = queryString.parse(search, memoizedParseOptions);
        return unflattenObject(parsed);
    }, [memoizedParseOptions]);

    const query = useMemo(() => parseQuery(location.search), [parseQuery, location.search]);

    /**
     * Sets the query
     * @type {(function(Dispatch<SetStateAction<Record<string, any>>>, {location?: LocationDescriptor<unknown>, replace?: boolean}=): void)}
     */
    const setQuery = useCallback((newQueryOrCallback, {location, replace = false} = {}) => {
        let newQuery;
        if (typeof newQueryOrCallback === 'function') {
            const prevQuery = parseQuery(window.location.search);
            newQuery = newQueryOrCallback(prevQuery);
        } else {
            newQuery = newQueryOrCallback;
        }

        // <-- Insert flattening here:
        newQuery = flattenObject(newQuery);

        const newLocation = {
            ...(!!location ? location : locationRef.current),
            search: !newQuery ? undefined : `?${queryString.stringify(newQuery, memoizedStringifyOptions)}`
        }
        if (deepEqual(newLocation, locationRef.current))
            return;
        if (!replace) {
            historyRef.current.push(newLocation);
        } else {
            historyRef.current.replace(newLocation);
        }
    }, [memoizedStringifyOptions, parseQuery])

    return [query, setQuery];
}

export default useQuery;
